export const SEV = {
  MAJOR: {
    label: "Major",
    cls: "bg-rose-50 text-rose-700 border-rose-200",
    solid: "bg-rose-600 text-white border-rose-600",
    dot: "bg-rose-600",
    hex: "#DC2626",
  },
  MINOR: {
    label: "Minor",
    cls: "bg-amber-50 text-amber-700 border-amber-200",
    solid: "bg-amber-500 text-white border-amber-500",
    dot: "bg-amber-500",
    hex: "#D97706",
  },
  COSMETIC: {
    label: "Cosmetic",
    cls: "bg-blue-50 text-blue-700 border-blue-200",
    solid: "bg-blue-600 text-white border-blue-600",
    dot: "bg-blue-600",
    hex: "#2563EB",
  },
  OBSERVATION: {
    label: "Observation",
    cls: "bg-violet-50 text-violet-700 border-violet-200",
    solid: "bg-violet-600 text-white border-violet-600",
    dot: "bg-violet-600",
    hex: "#7C3AED",
  },
  PASS: {
    label: "Pass",
    cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    solid: "bg-emerald-600 text-white border-emerald-600",
    dot: "bg-emerald-600",
    hex: "#059669",
  },
  NA: {
    label: "N/A",
    cls: "bg-slate-100 text-slate-600 border-slate-200",
    solid: "bg-slate-500 text-white border-slate-500",
    dot: "bg-slate-400",
    hex: "#64748B",
  },
  NT: {
    label: "Not tested",
    cls: "bg-zinc-100 text-zinc-600 border-zinc-200",
    solid: "bg-zinc-700 text-white border-zinc-700",
    dot: "bg-zinc-500",
    hex: "#3F3F46",
  },
};

export const DEFECT_SEVS = ["MAJOR", "MINOR", "COSMETIC", "OBSERVATION"];
const ORDER = { MAJOR: 0, MINOR: 1, COSMETIC: 2, OBSERVATION: 3 };
const WEIGHT = { MAJOR: 3, MINOR: 1, COSMETIC: 0.5, OBSERVATION: 0 };

export function liveSummary(
  ins,
  excludedRoomIds = [],
  excludedFindingIds = [],
) {
  const excluded = new Set(excludedRoomIds);
  const hiddenFindings = new Set(excludedFindingIds);
  const allDefects = [
    ...(ins?.defects || []),
    ...(ins?.original_defects || []).filter((d) =>
      ["NOT_RESOLVED", "PARTIAL"].includes(d.re_status),
    ),
  ];
  const defects = [...allDefects].filter(
    (defect) =>
      !excluded.has(defect.room_id) && !hiddenFindings.has(defect.defect_id),
  );
  const hiddenItemIds = new Set(
    allDefects
      .filter(
        (defect) => hiddenFindings.has(defect.defect_id) && defect.item_id,
      )
      .map((defect) => defect.item_id),
  );
  const activeItemIds = new Set(
    defects.filter((defect) => defect.item_id).map((defect) => defect.item_id),
  );
  const items = {
    total: 0,
    done: 0,
    PASS: 0,
    NA: 0,
    NT: 0,
    MAJOR: 0,
    MINOR: 0,
    COSMETIC: 0,
    OBSERVATION: 0,
    UNASSESSED: 0,
  };
  const rooms = (ins?.rooms || [])
    .filter((room) => !excluded.has(room.room_id))
    .map((r) => {
      const n = r.items.length;
      const done = r.items.filter((i) => i.result).length;
      items.total += n;
      items.done += done;
      const roomCounts = {
        MAJOR: 0,
        MINOR: 0,
        COSMETIC: 0,
        OBSERVATION: 0,
        PASS: 0,
        NA: 0,
        NT: 0,
        UNASSESSED: 0,
      };
      r.items.forEach((i) => {
        let result = i.result || "UNASSESSED";
        if (hiddenItemIds.has(i.item_id) && !activeItemIds.has(i.item_id))
          result = "PASS";
        if (!(result in items)) result = "UNASSESSED";
        if (result in items) items[result] += 1;
        if (result in roomCounts) roomCounts[result] += 1;
      });
      const rd = defects.filter((d) => d.room_id === r.room_id);
      return {
        ...r,
        total: n,
        done,
        progress: n ? Math.round((100 * done) / n) : 100,
        defects: rd.length,
        major: roomCounts.MAJOR,
        minor: roomCounts.MINOR,
        cosmetic: roomCounts.COSMETIC,
        observation: roomCounts.OBSERVATION,
        pass: roomCounts.PASS,
        na: roomCounts.NA,
        nt: roomCounts.NT,
        unassessed: roomCounts.UNASSESSED,
      };
    });
  const counts = {
    MAJOR: items.MAJOR,
    MINOR: items.MINOR,
    COSMETIC: items.COSMETIC,
    OBSERVATION: items.OBSERVATION,
  };
  const score = Math.max(
    0,
    Math.round(
      100 - defects.reduce((s, d) => s + (WEIGHT[d.severity] || 0), 0),
    ),
  );
  const scorableTotal = items.total - items.NA - items.NT;
  const scorableDone = items.done - items.NA - items.NT;
  const priority = [...defects]
    .sort((a, b) => (ORDER[a.severity] ?? 9) - (ORDER[b.severity] ?? 9))
    .slice(0, 5);
  return {
    counts,
    items,
    rooms,
    score,
    priority,
    total: defects.length,
    totalObservations: Object.values(counts).reduce(
      (sum, value) => sum + value,
      0,
    ),
    findingRecords: defects.length,
    scorableTotal,
    scorableDone: Math.max(0, scorableDone),
    excluded: items.NA + items.NT,
    progress: scorableTotal
      ? Math.round((100 * Math.max(0, scorableDone)) / scorableTotal)
      : 100,
    roomsAttention: rooms.filter((r) => r.major || r.minor).map((r) => r.name),
  };
}

export const worstSeverity = (defs) =>
  defs.map((d) => d.severity).sort((a, b) => ORDER[a] - ORDER[b])[0];

export const renumber = (defects) =>
  defects.map((d, i) => ({
    ...d,
    number: `PC-${String(i + 1).padStart(3, "0")}`,
  }));

export const STATUS = {
  scheduled: {
    label: "Scheduled",
    cls: "bg-slate-100 text-slate-700 border-slate-200",
  },
  in_progress: {
    label: "In progress",
    cls: "bg-amber-50 text-amber-700 border-amber-200",
  },
  completed: {
    label: "Completed",
    cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
};
